import {
  handleAffectsHeight,
  handleAffectsWidth,
  isBottomSideHandle,
  isLeftSideHandle,
  isRightSideHandle,
  isTopSideHandle,
  type ResizeHandle,
} from "../resizeDelta.ts";
import type { TransformOriginFractions } from "../transformMatrix.ts";
import { getOffsetOriginConfig, type OffsetOriginConfig } from "./config.ts";
import type { ComputedInset, InsetProperty, NumericInsets, OffsetOrigin } from "./types.ts";

const zeroInset: ComputedInset = { top: 0, right: 0, bottom: 0, left: 0 };

/** Read the browser-computed inset values (always resolved to px) from an element. */
export const getComputedInset = (element: HTMLElement | null): ComputedInset => {
  if (!element) return zeroInset;
  const style = getComputedStyle(element);
  return {
    top: parseFloat(style.top) || 0,
    right: parseFloat(style.right) || 0,
    bottom: parseFloat(style.bottom) || 0,
    left: parseFloat(style.left) || 0,
  };
};

/**
 * Linear part (a/b/c/d) of the cumulative CSS transform applied to the element.
 * Identity = `{ a: 1, b: 0, c: 0, d: 1 }`. Used to compensate for visual drift
 * when a rotated element's dimensions change and the rotation pivot moves.
 */
export type LinearMatrix = { a: number; b: number; c: number; d: number };

const IDENTITY_MATRIX: LinearMatrix = { a: 1, b: 0, c: 0, d: 1 };
const CENTER_ORIGIN: TransformOriginFractions = { ox: 0.5, oy: 0.5 };

/**
 * Anchor position along one axis as a fraction of the element's intrinsic
 * width/height. A right-side handle drag anchors the left edge (0); a left-side
 * handle drag anchors the right edge (1); a handle that doesn't move along this
 * axis leaves the visual center as the anchor (0.5).
 */
const horizontalAnchorFraction = (handle: ResizeHandle): number => {
  if (isRightSideHandle(handle)) return 0;
  if (isLeftSideHandle(handle)) return 1;
  return 0.5;
};

const verticalAnchorFraction = (handle: ResizeHandle): number => {
  if (isBottomSideHandle(handle)) return 0;
  if (isTopSideHandle(handle)) return 1;
  return 0.5;
};

export interface ComputeResizeInsetAdjustmentParams {
  offsetOrigin: OffsetOrigin | undefined;
  handle: ResizeHandle;
  widthDelta: number;
  heightDelta: number;
  computedInset: ComputedInset;
  /**
   * Linear part of the element's cumulative CSS transform. Defaults to identity
   * (no rotation/scale). When non-identity, the formula compensates for the
   * visual drift caused by `transform-origin` pivoting around the resized
   * element's new center/origin.
   */
  linearMatrix?: LinearMatrix;
  /**
   * `transform-origin` as fractions of intrinsic width/height. Defaults to
   * (0.5, 0.5) — the CSS default. Skew is not modelled; for rotate+scale this
   * is exact.
   */
  transformOrigin?: TransformOriginFractions;
}

/**
 * Numeric core of `getResizeInsetAdjustment`. Computes the CSS-layout shift
 * (`ΔCSS_left`, `ΔCSS_top`) that keeps the un-dragged edge visually anchored
 * after the element's intrinsic size changes, then expresses that shift through
 * the inset properties active for `offsetOrigin`.
 *
 * For unrotated elements (identity `linearMatrix`) the result matches the
 * pre-rotation behavior: only the dragged-side inset moves, by the dimension
 * delta. For rotated elements the formula also accounts for the rotation pivot
 * shifting when width/height change — without this the rotated element drifts
 * visually even though the layout-anchored edge "stayed put" in CSS-space.
 *
 * Math (per axis): for an anchor at local fraction `α` along x and rotation
 * pivot at `(ox·W, oy·H)`, the screen-x of the anchor changes by
 * `ΔCSS_left + ΔW·(ox + a·(α − ox)) + ΔH·c·(β − oy)`. Setting that to zero
 * gives `ΔCSS_left`. The vertical case is symmetric with `(b, d, oy, β)`.
 */
export const computeResizeInsetAdjustment = ({
  offsetOrigin,
  handle,
  widthDelta,
  heightDelta,
  computedInset,
  linearMatrix = IDENTITY_MATRIX,
  transformOrigin = CENTER_ORIGIN,
}: ComputeResizeInsetAdjustmentParams): NumericInsets | undefined => {
  if (!offsetOrigin) return undefined;
  const config = getOffsetOriginConfig(offsetOrigin);
  const { a, b, c, d } = linearMatrix;
  const { ox, oy } = transformOrigin;

  const affectsWidth = handleAffectsWidth(handle) && widthDelta !== 0;
  const affectsHeight = handleAffectsHeight(handle) && heightDelta !== 0;
  if (!affectsWidth && !affectsHeight) return undefined;

  const alpha = horizontalAnchorFraction(handle);
  const beta = verticalAnchorFraction(handle);

  // Screen-space shift the CSS layout must absorb to keep the anchor edge fixed.
  const deltaCssLeft =
    -(affectsWidth ? widthDelta * (ox + a * (alpha - ox)) : 0) - (affectsHeight ? heightDelta * c * (beta - oy) : 0);
  const deltaCssTop =
    -(affectsHeight ? heightDelta * (oy + d * (beta - oy)) : 0) - (affectsWidth ? widthDelta * b * (alpha - ox) : 0);

  const result: NumericInsets = {};
  let hasChanges = false;

  const writeInset = (property: InsetProperty, value: number) => {
    if (value === computedInset[property]) return;
    result[property] = value;
    hasChanges = true;
  };

  if (affectsWidth || (affectsHeight && (config.horizontalCenter || config.horizontalProperty))) {
    // Δright follows from `right = parent − width − left`, so any shift to CSS
    // left or any width change must flow into right too when right is active.
    if (config.horizontalProperty === "left") {
      writeInset("left", computedInset.left + deltaCssLeft);
    } else if (config.horizontalProperty === "right") {
      writeInset("right", computedInset.right - widthDelta - deltaCssLeft);
    } else if (config.horizontalCenter) {
      writeInset("left", computedInset.left + deltaCssLeft);
      writeInset("right", computedInset.right - widthDelta - deltaCssLeft);
    }
  }

  if (affectsHeight || (affectsWidth && (config.verticalCenter || config.verticalProperty))) {
    if (config.verticalProperty === "top") {
      writeInset("top", computedInset.top + deltaCssTop);
    } else if (config.verticalProperty === "bottom") {
      writeInset("bottom", computedInset.bottom - heightDelta - deltaCssTop);
    } else if (config.verticalCenter) {
      writeInset("top", computedInset.top + deltaCssTop);
      writeInset("bottom", computedInset.bottom - heightDelta - deltaCssTop);
    }
  }

  return hasChanges ? result : undefined;
};

type DragDelta = { x: number; y: number };

export interface ComputePositionUpdateParams {
  offsetOrigin: OffsetOrigin | undefined;
  delta: DragDelta;
  scale: number;
  computedInset: ComputedInset;
}

/**
 * Numeric core of `getPositionUpdateForDragDelta`. Computes the inset deltas
 * for a drag, respecting the active offset origin. Updates only the inset
 * properties relevant to the origin; inverts for right/bottom; centered axes
 * shift both insets symmetrically; falls back to top/left when no origin set.
 *
 * The browser-computed inset values are passed in as the base so non-px units
 * (%, em, calc) resolve to clean pixel values on first drag.
 */
export const computePositionUpdate = ({
  offsetOrigin,
  delta,
  scale,
  computedInset,
}: ComputePositionUpdateParams): NumericInsets => {
  if (!offsetOrigin) {
    return {
      left: computedInset.left + delta.x / scale,
      top: computedInset.top + delta.y / scale,
    };
  }

  const config = getOffsetOriginConfig(offsetOrigin);
  const result: NumericInsets = {};

  applyAxis({ result, axis: "horizontal", config, delta, scale, computedInset });
  applyAxis({ result, axis: "vertical", config, delta, scale, computedInset });

  return result;
};

interface ApplyAxisParams {
  result: NumericInsets;
  axis: "horizontal" | "vertical";
  config: OffsetOriginConfig;
  delta: DragDelta;
  scale: number;
  computedInset: ComputedInset;
}

const applyAxis = ({ result, axis, config, delta, scale, computedInset }: ApplyAxisParams) => {
  const isHorizontal = axis === "horizontal";
  const property = isHorizontal ? config.horizontalProperty : config.verticalProperty;
  const centered = isHorizontal ? config.horizontalCenter : config.verticalCenter;
  const axisDelta = isHorizontal ? delta.x : delta.y;
  const positiveSide: InsetProperty = isHorizontal ? "left" : "top";
  const negativeSide: InsetProperty = isHorizontal ? "right" : "bottom";

  if (centered) {
    result[positiveSide] = computedInset[positiveSide] + axisDelta / scale;
    result[negativeSide] = computedInset[negativeSide] - axisDelta / scale;
    return;
  }

  if (!property) return;
  const sign = property === negativeSide ? -1 : 1;
  result[property] = computedInset[property] + (sign * axisDelta) / scale;
};
